import { PROJECT_STAGE_DEFINITIONS } from './project-stages.js';
import { CANONICAL_SIGNAL_TYPE_VALUES, SIGNAL_TYPE_REGISTRY } from './signal-type-registry.js';
import type { SignalType } from './signal-types.js';

export const SIGNAL_PRIORITY_VALUES = ['high', 'medium', 'low'] as const;
export type SignalPriority = (typeof SIGNAL_PRIORITY_VALUES)[number];

/** The default signal history window in days; a plan's entitlements can tighten it. */
export const SIGNAL_QUEUE_FILED_FLOOR_DAYS = 90;

export interface RecommendedSignalSubscription {
  signalType: SignalType;
  priority: SignalPriority;
}

/**
 * Pre-checked defaults for the onboarding Signals step, derived from the
 * registry's recommendedPriority. flatMap filters out null priorities so the
 * resulting `priority` is the non-null SignalPriority with no unsafe cast.
 */
export const RECOMMENDED_SIGNAL_SUBSCRIPTIONS: readonly RecommendedSignalSubscription[] =
  CANONICAL_SIGNAL_TYPE_VALUES.flatMap((signalType) => {
    const priority = SIGNAL_TYPE_REGISTRY[signalType].recommendedPriority;
    return priority === null ? [] : [{ signalType, priority }];
  });

/** Signal types users can subscribe to (registry-driven; excludes reserved types). */
export const SUBSCRIBABLE_SIGNAL_TYPES: readonly SignalType[] = CANONICAL_SIGNAL_TYPE_VALUES.filter(
  (signalType) => SIGNAL_TYPE_REGISTRY[signalType].subscribable,
);

/** A stage-ordered bucket of subscribable signal types for the picker UI. */
export interface SignalTypeGroup {
  /** Stage value, or 'outcomes' (terminal off-ramps) / 'other' (no stage position). */
  key: string;
  /** Group heading. */
  label: string;
  signalTypes: SignalType[];
}

/**
 * Group the subscribable signal types by the stage they drive, in funnel order
 * (the 7 rungs; demolition_permit / site_prep ride in Permitted), followed by
 * Outcomes (terminal-disposition signals) and Other (the remaining null-stage
 * types: permit_filed, team_update). Stage and disposition are read from
 * SIGNAL_TYPE_REGISTRY so grouping tracks the same source of truth
 * deriveProjectStatus consumes; empty groups are omitted. Not every subscribable
 * type lands on a stage rung; the null-stage non-terminal ones go to Other.
 */
export function groupSubscribableSignalTypesByStage(): SignalTypeGroup[] {
  const stageGroups: SignalTypeGroup[] = PROJECT_STAGE_DEFINITIONS.map((definition) => ({
    key: definition.value,
    label: definition.label,
    signalTypes: [],
  }));
  const outcomes: SignalTypeGroup = { key: 'outcomes', label: 'Outcomes', signalTypes: [] };
  const other: SignalTypeGroup = { key: 'other', label: 'Other', signalTypes: [] };

  for (const signalType of SUBSCRIBABLE_SIGNAL_TYPES) {
    const entry = SIGNAL_TYPE_REGISTRY[signalType];
    if (entry.stage !== null) {
      // stage is always one of PROJECT_STAGE_DEFINITIONS, so a group exists.
      stageGroups.find((group) => group.key === entry.stage)?.signalTypes.push(signalType);
    } else if (entry.terminalDisposition !== null) {
      outcomes.signalTypes.push(signalType);
    } else {
      other.signalTypes.push(signalType);
    }
  }

  return [...stageGroups, outcomes, other].filter((group) => group.signalTypes.length > 0);
}
